import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Box, Button, Icon, Stack } from "@chakra-ui/react";
import { MdCancel, MdRestartAlt } from "react-icons/md";
import CancelSubscriptionDialog from "./CancelSubscriptionDialog";
import { ORANGE_MONEY_PROVIDER } from "./subscriptionsConstants";

const dangerButtonProps = {
    background: "rgba(244, 67, 54, 0.15)",
    color: "rgba(229, 115, 115, 1)",
    border: "1px solid rgba(244, 67, 54, 0.4)",
    fontWeight: 600,
    _hover: { background: "rgba(244, 67, 54, 0.25)" },
    _disabled: { opacity: 0.5 },
};

const successButtonProps = {
    background: "rgba(76, 175, 80, 0.15)",
    color: "rgba(129, 199, 132, 1)",
    border: "1px solid rgba(76, 175, 80, 0.4)",
    fontWeight: 600,
    _hover: { background: "rgba(76, 175, 80, 0.25)" },
    _disabled: { opacity: 0.5 },
};

/**
 * Determines whether the cancel button should be shown.
 * Allowed for active or pending subscriptions.
 * @param {Object} sub - Subscription object
 * @returns {boolean}
 */
function canCancel(sub) {
    return sub?.status === "active" || sub?.status === "pending";
}

/**
 * Determines whether the reactivate button should be shown.
 * Per spec: visible only when auto_renew=false AND status=active.
 * Orange Money prepaid rows have no recurrence to restore (the API refuses
 * them with a 400), so the button is hidden for them.
 * @param {Object} sub - Subscription object
 * @returns {boolean}
 */
function canReactivate(sub) {
    if (sub?.payment_provider === ORANGE_MONEY_PROVIDER) return false;
    return sub?.status === "active" && sub?.auto_renew === false;
}

/**
 * Calls the reactivate endpoint with toast feedback.
 * @param {Object} args - Reactivation arguments
 * @returns {Promise<void>}
 */
async function submitReactivate({ subscriptionId, setLoading, onChanged }) {
    setLoading(true);
    try {
        const { data } = await axios.post(`/admin/subscriptions/${subscriptionId}/reactivate`);
        toast.success("Abonnement réactivé");
        onChanged?.(data);
    } catch (error) {
        const msg = error?.response?.data || "Échec de la réactivation";
        toast.error(typeof msg === "string" ? msg : "Échec de la réactivation");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders cancel/reactivate buttons based on the subscription state and
 * mounts the cancel dialog. Both actions trigger onChanged on success.
 * @param {Object} props
 * @param {Object} props.sub - Subscription object
 * @param {Function} props.onChanged - Callback after a successful action
 * @returns {React.ReactElement|null}
 */
function SubscriptionActions({ sub, onChanged }) {
    const [cancelOpen, setCancelOpen] = useState(false);
    const [reactivating, setReactivating] = useState(false);
    const showCancel = canCancel(sub);
    const showReactivate = canReactivate(sub);

    if (!showCancel && !showReactivate) return null;

    const handleReactivate = () =>
        submitReactivate({ subscriptionId: sub.id, setLoading: setReactivating, onChanged });

    return (
        <Box pt={2}>
            <Stack direction="row" spacing={3}>
                {showCancel && (
                    <Button onClick={() => setCancelOpen(true)} leftIcon={<Icon as={MdCancel} />}
                        {...dangerButtonProps}>
                        Annuler
                    </Button>
                )}
                {showReactivate && (
                    <Button onClick={handleReactivate} isDisabled={reactivating} leftIcon={<Icon as={MdRestartAlt} />}
                        {...successButtonProps}>
                        {reactivating ? "Réactivation..." : "Réactiver"}
                    </Button>
                )}
            </Stack>
            <CancelSubscriptionDialog open={cancelOpen} onClose={() => setCancelOpen(false)}
                subscriptionId={sub?.id} onCancelled={onChanged} />
        </Box>
    );
}

export default SubscriptionActions;
